import * as React from "react";
import { Edit, Plus } from "lucide-react";
import { Avatar } from "../../../ui/components/Avatar";
import { Card } from "../../../ui/components/Card";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction, ListViewHeaderAction } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import { Text } from "../../../ui/components/Text";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { useI18n } from "../../lib/i18n";
import { useWebSocket } from "../../hooks/useWebSocket";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import * as subscriptionsApi from "../../lib/api/subscriptions";
import type { ProductSubscriberRow } from "../../lib/api/subscriptions";
import * as s from "./ProductPage.css";

export interface ProductSubscribersSectionProps {
  productId: string;
  token: string | null;
  hideChrome?: boolean;
  refreshKey?: number;
  onEdit?: (userId: string, displayName: string) => void;
  /** Open modal with no user prefilled (product locked). */
  onAddSubscription?: () => void;
  onSubscriberCountChange?: (count: number) => void;
}

export function ProductSubscribersSection({
  productId,
  token,
  hideChrome = false,
  refreshKey = 0,
  onEdit,
  onAddSubscription,
  onSubscriberCountChange,
}: ProductSubscribersSectionProps) {
  const { t } = useI18n();
  const [rows, setRows] = React.useState<ProductSubscriberRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);
  const { subscribe, unsubscribe } = useWebSocket();

  const loadRows = React.useCallback(async () => {
    if (!token) return;
    const data = await subscriptionsApi.fetchProductSubscribers(token, productId);
    setRows(data);
    setListRefreshKey((k) => k + 1);
  }, [token, productId]);

  const reload = React.useCallback(async () => {
    try {
      setError(null);
      await loadRows();
    } catch (e) {
      setError(getApiErrorMessage(e, t("pro.products.subscriptions.error.loadSubscribers")));
    }
  }, [loadRows, t]);

  React.useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void subscriptionsApi
      .fetchProductSubscribers(token, productId)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setListRefreshKey((k) => k + 1);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(getApiErrorMessage(e, t("pro.products.subscriptions.error.loadSubscribers")));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, productId, t, refreshKey]);

  React.useEffect(() => {
    onSubscriberCountChange?.(rows.length);
  }, [rows.length, onSubscriberCountChange]);

  React.useEffect(() => {
    const topic = `entity:subscriptions:product:${productId}`;
    const handler = () => {
      void reload();
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, subscribe, unsubscribe, reload]);

  const fetchList = React.useCallback(
    async (params: ListParams): Promise<ListResult<ProductSubscriberRow & { id: string }>> => {
      const q = (params.search ?? "").trim().toLowerCase();
      const filtered = q
        ? rows.filter(
            (r) =>
              r.displayName.toLowerCase().includes(q) ||
              r.email.toLowerCase().includes(q),
          )
        : rows;
      return {
        data: filtered.map((r) => ({ ...r, id: r.userId })),
        total: filtered.length,
      };
    },
    [rows],
  );

  const listContextKey = React.useMemo(
    () => `${listRefreshKey}:${rows.map((r) => `${r.userId}:${r.eventCount}`).join(",")}`,
    [listRefreshKey, rows],
  );

  const columns = React.useMemo<ColumnDefinition<ProductSubscriberRow & { id: string }>[]>(
    () => [
      {
        id: "user",
        label: t("pro.products.subscriptions.user"),
        render: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar
              email={row.email}
              firstName={row.firstName}
              lastName={row.lastName}
              image={normalizeAvatarUrl(row.avatarUrl) || undefined}
              size={28}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{row.displayName}</div>
              <div style={{ fontSize: 12, color: "var(--color-fgSecondary)" }}>{row.email}</div>
            </div>
          </div>
        ),
      },
      {
        id: "notifications",
        label: t("pro.products.subscriptions.notifications"),
        render: (row) => (
          <span style={{ fontSize: 13 }}>
            {row.eventCount}{" "}
            {row.eventCount === 1
              ? t("pro.products.subscriptions.notification")
              : t("pro.products.subscriptions.notificationsCount")}
          </span>
        ),
      },
    ],
    [t],
  );

  const headerActions = React.useMemo<ListViewHeaderAction[] | undefined>(() => {
    if (!onAddSubscription) return undefined;
    return [
      {
        id: "add-subscription",
        label: t("pro.products.subscriptions.addSubscription"),
        icon: <Plus size={14} />,
        color: "primary",
        onClick: onAddSubscription,
      },
    ];
  }, [onAddSubscription, t]);

  const getContextActions = React.useCallback(
    (row: ProductSubscriberRow & { id: string }): ListViewContextAction[] => {
      if (!onEdit) return [];
      return [
        {
          id: "edit",
          label: t("pro.products.subscriptions.edit"),
          icon: <Edit size={14} />,
          onClick: () => onEdit(row.userId, row.displayName),
          bulk: false,
        },
      ];
    },
    [onEdit, t],
  );

  const body = (
    <>
      {error ? (
        <div style={{ marginBottom: 12 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      ) : null}

      {loading ? (
        <Text level="body-small">{t("shared.common.loading")}</Text>
      ) : (
        <ListView<ProductSubscriberRow & { id: string }>
          listId={`product-subscribers-${productId}`}
          columns={columns}
          fetchList={fetchList}
          contextKey={listContextKey}
          queryOptions={{ staleTime: Infinity }}
          enabledViews={["stack", "table"]}
          headerActions={headerActions}
          getContextActions={getContextActions}
          emptyTitle={t("pro.products.subscriptions.noSubscribers")}
          renderStackCard={(row, ctx) => {
            const image = (
              <Avatar
                email={row.email}
                firstName={row.firstName}
                lastName={row.lastName}
                image={normalizeAvatarUrl(row.avatarUrl) || undefined}
                size={ctx.compact ? 40 : 72}
              />
            );

            const eventBadge = {
              id: "events",
              label: `${row.eventCount} ${
                row.eventCount === 1
                  ? t("pro.products.subscriptions.notification")
                  : t("pro.products.subscriptions.notificationsCount")
              }`,
              variant: "default" as const,
            };

            return (
              <ListViewCard
                id={row.userId}
                layout="stack"
                imageFormat="avatar"
                image={image}
                title={row.displayName}
                subtitle={row.email}
                badges={[eventBadge]}
                compact={ctx.compact}
                onClick={onEdit ? () => onEdit(row.userId, row.displayName) : undefined}
              />
            );
          }}
          compact
          disableLocalPersistence
          defaultPageSize={10}
        />
      )}
    </>
  );

  if (hideChrome) {
    return body;
  }

  return (
    <Card maxWidth={960} className={s.sectionCard}>
      <div className={s.cardInner}>{body}</div>
    </Card>
  );
}
